import { useEffect, useRef } from "react"

/*
	El boton "atras" del telefono cierra la ficha abierta.

	Las fichas de WAPP (ver un horario, matricular, editar un curso) se abren
	encima de la lista. En un telefono eso se ve y se usa como si fuera otra
	pagina, asi que la gente aprieta "atras" para volver a la lista... y salia de
	WAPP entera. (Esteban, 2026-09-20)

	La idea: mientras haya una ficha abierta, dejamos puesta una parada en el
	historial del navegador. Si aprietan "atras", el navegador vuelve a esa parada,
	nosotros lo oimos y cerramos la ficha en vez de salir. La lista de atras nunca
	se recarga: sigue viva debajo, con su scroll y sus filtros intactos.

	Por que se lleva la cuenta en vez de poner y quitar la parada en el acto:

	1. React en desarrollo monta cada componente, lo desmonta y lo vuelve a montar
	   para cazar errores. Si cada montaje pusiera una parada y cada desmontaje la
	   quitara, el quitar del desmontaje falso llegaria tarde y cerraria la ficha
	   recien abierta. Con una cuenta que se cuadra en el siguiente respiro, los
	   tres pasos se cancelan y queda una sola parada, que es la correcta.
	2. Con dos fichas abiertas una encima de otra, la cuenta tambien cuadra sola.

	Se conserva el resto del estado del historial (el "idx" de react-router): si se
	lo quitamos, el router pierde la cuenta de en que paso va.
*/

type Ficha = {cerrar : () => void}

//Las fichas abiertas, de la de mas abajo a la de mas arriba.
const pila : Ficha[] = []

//Paradas que de verdad estan puestas en el historial.
let paradasPuestas = 0

//Retrocesos que pedimos nosotros: su aviso no hay que confundirlo con el del usuario.
let retrocesosPropios = 0

let cuadreProgramado = false
let escuchando       = false

const cuadrar = () =>
{
	cuadreProgramado = false

	while(pila.length > paradasPuestas)
	{
		window.history.pushState({...(window.history.state || {}), waFicha : true}, "")
		paradasPuestas++
	}

	while(pila.length < paradasPuestas)
	{
		paradasPuestas--

		/*
			Solo se retrocede si la parada de arriba sigue siendo NUESTRA.

			Hay pantallas que cambian la direccion mientras la ficha esta abierta
			-- Alumnos escribe /students?alumno=... para poder compartir el perfil --
			y al hacerlo reemplazan nuestra parada. Si aun asi retrocedieramos,
			volveriamos a la direccion anterior, que todavia lleva el ?alumno=..., y
			la pantalla volveria a abrir la misma ficha: la X parecia no funcionar.
			(reportado por Esteban, 2026-09-20)

			Cuando la parada ya no es nuestra, se suelta la cuenta y no se toca el
			historial. Lo peor que puede pasar es que quede una parada de mas, que
			solo cuesta un "atras" sin efecto.
		*/
		if(!window.history.state || window.history.state.waFicha !== true)
		{
			continue
		}

		retrocesosPropios++
		window.history.back()
	}
}

//En la siguiente microtarea: para entonces React ya termino de montar y desmontar.
const programarCuadre = () =>
{
	if(cuadreProgramado) return

	cuadreProgramado = true
	Promise.resolve().then(cuadrar)
}

const alVolver = () =>
{
	if(retrocesosPropios > 0)
	{
		retrocesosPropios--
		return
	}

	if(pila.length < 1) return

	paradasPuestas = Math.max(0, paradasPuestas - 1)
	pila[pila.length - 1].cerrar()
}

const escuchar = () =>
{
	if(escuchando || typeof window === "undefined") return

	escuchando = true
	window.addEventListener("popstate", alVolver)
}

export const useAtrasCierra = (cerrar : () => void, activo : boolean = true) : void =>
{
	//La funcion de cerrar cambia en cada render; el efecto no debe reiniciarse por eso.
	const cerrarRef = useRef(cerrar)

	cerrarRef.current = cerrar

	useEffect
	(
		() =>
		{
			if(!activo || typeof window === "undefined") return

			escuchar()

			const ficha : Ficha = {cerrar : () => cerrarRef.current()}

			pila.push(ficha)
			programarCuadre()

			return () =>
			{
				const donde = pila.indexOf(ficha)

				if(donde >= 0) pila.splice(donde, 1)

				programarCuadre()
			}
		},
		[activo]
	)
}

export default useAtrasCierra
